import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { Logo } from "@/components/layout/Logo";
import { PrimaryButton, InputField, TextLink } from "@/components/fionova/Primitives";
import { useAuth } from "@/context/AuthContext";
import { afterJourneyAuth, journey } from "@/lib/journey";

export default function SignIn() {
  const navigate = useNavigate();
  const { login, refresh } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const canSubmit = form.email.trim() && form.password.length >= 6 && !busy;

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const u = await login({ email: form.email.trim(), password: form.password });
      const next = new URLSearchParams(window.location.search).get("next");
      if (next === "admin") navigate("/admin/go-live");
      else if (next === "journey" || journey.guestId()) await afterJourneyAuth(navigate, refresh);
      else if (u.entitled) navigate("/app");
      else if (u.journey_status === "completed") navigate("/journey/complete");
      else if (next === "journey" || u.journey_status) navigate("/journey");
      else if (u.has_discovery) navigate("/discovery/reveal");
      else navigate("/");
    } catch (e) {
      setError(e.message || "Sign in failed.");
      setBusy(false);
    }
  };

  return (
    <OnboardingLayout immersive testId="page-signin" onBack={() => navigate("/")}>
      <div className="flex flex-col items-center pt-2 text-center">
        <Logo size={72} />
        <h1 className="mt-8 text-3xl font-semibold tracking-tight text-foreground">
          Welcome back
        </h1>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Sign in to continue your journey.
        </p>
      </div>

      <div
        className="mt-10 flex flex-col gap-4"
        onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") { e.preventDefault(); if (canSubmit) submit(); } }}
      >
        <InputField
          label="Email" testId="signin-email" type="email" autoComplete="email"
          placeholder="you@example.com"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <InputField
          label="Password" testId="signin-password" type="password" autoComplete="current-password"
          placeholder="Your password"
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        {error && <p className="text-sm text-red-600" data-testid="signin-error">{error}</p>}
        <PrimaryButton data-testid="signin-submit" onClick={() => canSubmit && submit()} disabled={!canSubmit} className="mt-2">
          {busy ? "Signing in…" : "Sign in"}
        </PrimaryButton>
      </div>

      <div className="mt-5 flex flex-col items-center gap-3 text-center">
        <TextLink data-testid="signin-forgot" onClick={() => navigate("/forgot-password")}>
          Forgot password?
        </TextLink>
        <TextLink data-testid="signin-to-register" onClick={() => navigate(`/begin${window.location.search}`)}>
          New here? Begin your journey
        </TextLink>
      </div>
    </OnboardingLayout>
  );
}
